/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface AssignmentDelayedReasonProps {
  id: string;
  name: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class AssignmentDelayedReasonRecord extends Record<AssignmentDelayedReasonProps>({
  id: null as any,
  name: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): AssignmentDelayedReasonRecord {
    if (json == null) {
      throw new Error('AssignmentDelayedReasonRecord.fromJS: parameter cannot be null');
    }
    return new AssignmentDelayedReasonRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'AssignmentDelayedReasonJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'AssignmentDelayedReasonJson.name'),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [AssignmentDelayedReasonRecord, Error[]] {
    if (json == null) {
      throw new Error('AssignmentDelayedReasonRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new AssignmentDelayedReasonRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'AssignmentDelayedReasonJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'AssignmentDelayedReasonJson.name'),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: AssignmentDelayedReasonProps) {
    super(props);
    if (props == null) {
      throw new Error('AssignmentDelayedReasonRecord constructor parameter cannot be null');
    }
  }
}

export abstract class AssignmentDelayedReasonViewModelBase implements AssignmentDelayedReasonProps {
  protected readonly _props: AssignmentDelayedReasonProps;
  private _json?: AssignmentDelayedReasonProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: AssignmentDelayedReasonProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllAssignmentDelayedReasonEntities = (state: AppState) =>
  state.entityStores.assignmentDelayedReason.entities;

export const selectAssignmentDelayedReasonById = (id: string) => (state: AppState) =>
  state.entityStores.assignmentDelayedReason.entities.get(id);

export const selectAssignmentDelayedReasonMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.assignmentDelayedReason.meta.get(id);

export const selectAssignmentDelayedReasonAndMetadataById = (id: string) =>
  createSelector(
    selectAssignmentDelayedReasonById(id),
    selectAssignmentDelayedReasonMetadataById(id),
    (assignmentDelayedReason, meta) => ({ assignmentDelayedReason, meta }),
  );

export function selectAssignmentDelayedReasonEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllAssignmentDelayedReasonEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, AssignmentDelayedReasonRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectAssignmentDelayedReasonEntitiesByName(name: string) {
  return createSelector(selectAllAssignmentDelayedReasonEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}
//#endregion Selectors
